import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';

import type { NotificationType } from './use-notifications';

const PREFERENCES_QUERY_KEY = 'notification-preferences';
const ERROR_TOAST_TITLE_KEY = 'preferences.errorTitle';
const AUTH_ERROR = 'User or organization not found';
const UPSERT_CONFLICT = 'user_id,organization_id';
// MUST mirror the settings UI's default-on set (ALL_NOTIFICATION_TYPES, derived
// from NOTIFICATION_TYPE_GROUPS in notification-preferences-section.tsx) — this
// is the fallback when a prefs row is first written (e.g. toggling DND before
// visiting the Types section), so a mismatch makes a type's default depend on
// which control the user touches first. Excludes the dead draft_rejected.
// thread_comment is the one exception until its switch ships: it is on by default
// before anyone can turn it off (docs/plans/2026-11-01-feat-ios-push-work-events-plan.md, D5).
const DEFAULT_ENABLED_TYPES: NotificationType[] = [
  'comment_mention',
  'task_assigned',
  'email_assigned',
  'correspondence_shared',
  'watched_thread_activity',
  'draft_approval_requested',
  'draft_changes_requested',
  'draft_approved',
  'pulse_ai_approval_requested',
  'email_account_disconnected',
  'gdrive_disconnected',
  'knowledge_source_attention',
  'messaging_account_disconnected',
  'external_reply_marked_unsent',
  'scheduled_send_failed',
  'scheduled_send_stranded',
  'office_messenger_direct_message',
  'office_messenger_mention',
  'office_messenger_thread_reply',
  'pulse_ai_run_completed',
  'inbox_email_received',
  'thread_comment',
];

/**
 * Per-type notification sub-criteria. A criterion NARROWS which events of a
 * given type produce a notification — it never widens. Absent ⇒ no filter ⇒
 * the type fires as it always did (fully backward-compatible). The `{ op, values }`
 * shape mirrors the correspondence-list filter grammar so vocabularies stay in sync.
 */
export type NotificationCriteriaOp = 'is' | 'is_not';

export interface NotificationCriteriaClause {
  op: NotificationCriteriaOp;
  values: string[];
}

export type NotificationAssignmentScope = 'named_only' | 'any';

export interface DraftApprovalCriteria {
  priority?: NotificationCriteriaClause;
  sentiment?: NotificationCriteriaClause;
  category?: NotificationCriteriaClause;
  assignment_scope?: NotificationAssignmentScope;
  requester?: NotificationCriteriaClause;
}

export type NotificationTypeCriteria = Partial<Record<NotificationType, DraftApprovalCriteria>>;

export interface NotificationPreferences {
  id: string;
  user_id: string;
  organization_id: string;
  dnd_enabled: boolean;
  dnd_until: string | null;
  enabled_types: NotificationType[];
  type_criteria: NotificationTypeCriteria;
  ios_push_enabled: boolean;
  ios_push_rich_previews_enabled: boolean;
  ios_push_time_sensitive_enabled: boolean;
  sound_enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Normalise a raw `notification_preferences` row at every read/write boundary.
 * Older rows may surface `type_criteria` as null in JS even though the column
 * defaults to '{}'; coerce to an empty object so consumers never guard against null.
 */
// Strictly non-null: both callers null-guard before this, and a non-null return
// keeps downstream callers from treating a normalized row as possibly-null.
function normalizePreferences(row: NotificationPreferences): NotificationPreferences {
  return {
    ...row,
    type_criteria: row.type_criteria ?? {},
  };
}

export function useNotificationPreferences() {
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();

  // Skip query when in admin mode (currentOrganizationId === 'admin')
  const isValidOrgId = currentOrganizationId && currentOrganizationId !== 'admin';

  return useQuery({
    queryKey: [PREFERENCES_QUERY_KEY, currentOrganizationId, user?.id],
    queryFn: async () => {
      if (!user?.id || !isValidOrgId) {
        return null;
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();

      if (error) {
        console.error('[useNotificationPreferences] Error:', error);
        throw error;
      }

      if (!data) {
        return null;
      }

      return normalizePreferences(data as NotificationPreferences);
    },
    enabled: !!user?.id && !!isValidOrgId,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: async (
      preferences: Partial<
        Omit<
          NotificationPreferences,
          'id' | 'user_id' | 'organization_id' | 'created_at' | 'updated_at'
        >
      >,
    ) => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        throw new Error(AUTH_ERROR);
      }

      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert(
          {
            user_id: user.id,
            organization_id: currentOrganizationId,
            ...preferences,
          },
          {
            onConflict: UPSERT_CONFLICT,
          },
        )
        .select()
        .single();

      if (error) {
        console.error('[useUpdateNotificationPreferences] Error:', error);
        throw error;
      }

      // Normalize at the write boundary so BOTH the cache seed below and any
      // per-call onSuccess(data) handler see the same shape (type_criteria never
      // null), honouring the normalize-at-every-boundary contract.
      return normalizePreferences(data as NotificationPreferences);
    },
    onSuccess: (data) => {
      // Seed the cache with the fresh (already-normalised) row synchronously.
      // TanStack runs this hook-level onSuccess BEFORE the per-call onSuccess
      // (which resets the component's local criteria to null), so the fallback
      // serverTypeCriteria is already fresh when local resets — no
      // flash-back-to-stale flicker. The invalidate below then confirms against
      // the server (belt-and-braces).
      // Key off the returned row's own org/user, not the closure's context —
      // context can change before the mutation resolves, and this avoids ever
      // seeding a [..., undefined] key.
      queryClient.setQueryData([PREFERENCES_QUERY_KEY, data.organization_id, data.user_id], data);
      queryClient
        .invalidateQueries({
          queryKey: [PREFERENCES_QUERY_KEY],
        })
        .catch(() => null);
      toast({
        title: t('preferences.savedTitle'),
        description: t('preferences.savedBody'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useToggleDND() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: async ({ enabled, until }: { enabled: boolean; until?: Date | null }) => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        throw new Error(AUTH_ERROR);
      }

      // First check if preferences exist
      const { data: existing } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert(
          {
            user_id: user.id,
            organization_id: currentOrganizationId,
            dnd_enabled: enabled,
            dnd_until: until ? until.toISOString() : null,
            // Preserve existing values or use defaults
            sound_enabled: existing?.sound_enabled ?? true,
            enabled_types: existing?.enabled_types ?? DEFAULT_ENABLED_TYPES,
          },
          {
            onConflict: UPSERT_CONFLICT,
          },
        )
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data as NotificationPreferences;
    },
    onSuccess: (data) => {
      queryClient
        .invalidateQueries({
          queryKey: [PREFERENCES_QUERY_KEY],
        })
        .catch(() => null);

      let message = t('preferences.dndDisabled');
      if (data.dnd_enabled) {
        message = data.dnd_until
          ? t('preferences.dndEnabledUntil', {
              date: new Date(data.dnd_until).toLocaleString(),
            })
          : t('preferences.dndEnabled');
      }

      toast({
        title: t('preferences.settingsUpdatedTitle'),
        description: message,
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

export function useToggleNotificationSound() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();
  const { toast } = useToast();
  const { t } = useTranslation('notifications');

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      if (!user?.id || !currentOrganizationId || currentOrganizationId === 'admin') {
        throw new Error(AUTH_ERROR);
      }

      // First check if preferences exist
      const { data: existing } = await supabase
        .from('notification_preferences')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganizationId)
        .maybeSingle();

      const { data, error } = await supabase
        .from('notification_preferences')
        .upsert(
          {
            user_id: user.id,
            organization_id: currentOrganizationId,
            sound_enabled: enabled,
            // Preserve existing values or use defaults
            dnd_enabled: existing?.dnd_enabled ?? false,
            dnd_until: existing?.dnd_until ?? null,
            enabled_types: existing?.enabled_types ?? DEFAULT_ENABLED_TYPES,
          },
          {
            onConflict: UPSERT_CONFLICT,
          },
        )
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data as NotificationPreferences;
    },
    onSuccess: (data) => {
      // Invalidate all notification preference queries
      queryClient
        .invalidateQueries({
          queryKey: [PREFERENCES_QUERY_KEY],
        })
        .catch(() => null);

      toast({
        title: t('preferences.settingsUpdatedTitle'),
        description: data.sound_enabled
          ? t('preferences.soundsEnabled')
          : t('preferences.soundsDisabled'),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t(ERROR_TOAST_TITLE_KEY),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}
